import {
  Box,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
  Tooltip,
  Flex,
} from '@chakra-ui/react'
import { QuestionIcon } from '@chakra-ui/icons'
import React, { useState, useEffect } from 'react'

interface AmalgamsSectionProps {
  currentNumAmalgamFillings: string
  setCurrentNumAmalgamFillings: (value: string) => void
  amalgamTotalTeeth: string
  setAmalgamTotalTeeth: (value: string) => void
  firstAmalgamAge: string
  setFirstAmalgamAge: (value: string) => void
  lastAmalgamAge: string
  setLastAmalgamAge: (value: string) => void
  computedYearsExposed: string | number
  computedExposureNumber: string | number
  amalgamError: boolean
  isSaved: boolean
  adjustedAmalgamFillings?: string
  adjustedTotalAmalgamFillings?: string
  hasAmalgamAdjustments?: boolean
  hasTotalAmalgamAdjustments?: boolean
}

const AmalgamsSection: React.FC<AmalgamsSectionProps> = ({
  currentNumAmalgamFillings,
  setCurrentNumAmalgamFillings,
  amalgamTotalTeeth,
  setAmalgamTotalTeeth,
  firstAmalgamAge,
  setFirstAmalgamAge,
  lastAmalgamAge,
  setLastAmalgamAge,
  computedYearsExposed,
  computedExposureNumber,
  amalgamError,
  isSaved,
  adjustedAmalgamFillings,
  adjustedTotalAmalgamFillings,
  hasAmalgamAdjustments,
  hasTotalAmalgamAdjustments,
}) => {
  // Helper function to determine if a field should be disabled
  // Only disable fields when the form has been saved AND the field has a value
  const isFieldDisabled = (value: string) => {
    return isSaved && value !== ''
  }

  // Internal state for handling input changes
  const [
    workingCurrentNumAmalgamFillings,
    setWorkingCurrentNumAmalgamFillings,
  ] = useState(currentNumAmalgamFillings)
  const [workingAmalgamTotalTeeth, setWorkingAmalgamTotalTeeth] =
    useState(amalgamTotalTeeth)
  const [workingFirstAmalgamAge, setWorkingFirstAmalgamAge] =
    useState(firstAmalgamAge)
  const [workingLastAmalgamAge, setWorkingLastAmalgamAge] =
    useState(lastAmalgamAge)

  // Update internal state when props change (for initial load and adjustments)
  useEffect(() => {
    if (isSaved && adjustedAmalgamFillings) {
      setWorkingCurrentNumAmalgamFillings(adjustedAmalgamFillings)
    } else {
      setWorkingCurrentNumAmalgamFillings(currentNumAmalgamFillings)
    }
  }, [isSaved, currentNumAmalgamFillings, adjustedAmalgamFillings])

  useEffect(() => {
    if (isSaved && adjustedTotalAmalgamFillings) {
      setWorkingAmalgamTotalTeeth(adjustedTotalAmalgamFillings)
    } else {
      setWorkingAmalgamTotalTeeth(amalgamTotalTeeth)
    }
  }, [isSaved, amalgamTotalTeeth, adjustedTotalAmalgamFillings])

  useEffect(() => {
    setWorkingFirstAmalgamAge(firstAmalgamAge)
  }, [firstAmalgamAge])

  useEffect(() => {
    setWorkingLastAmalgamAge(lastAmalgamAge)
  }, [lastAmalgamAge])

  return (
    <>
      <Text fontSize="lg" fontWeight="bold" mt={2}>
        Amalgams{' '}
        <Tooltip
          label="It is strongly encouraged to remove only one dental amalgam at once by a
      biological dentist and spaced out over months or years to limit too much
      mercury exposure at once."
        >
          <QuestionIcon color="gray.400" ml={2} />
        </Tooltip>
      </Text>
      <Text mb={4}></Text>
      <SimpleGrid columns={1} spacing={4}>
        <Flex>
          <FormLabel w="50%">
            1. Current number of amalgam fillings{' '}
            <Tooltip label="Open your mouth and count the number of silver fillings. Then add estimated 'silver' fillings under crowns.">
              <QuestionIcon color="gray.400" ml={1} />
            </Tooltip>
          </FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter current number"
              value={workingCurrentNumAmalgamFillings}
              onChange={(e) => {
                if (!isFieldDisabled(currentNumAmalgamFillings)) {
                  setWorkingCurrentNumAmalgamFillings(e.target.value)
                }
              }}
              onBlur={() =>
                setCurrentNumAmalgamFillings(workingCurrentNumAmalgamFillings)
              }
              disabled={isFieldDisabled(currentNumAmalgamFillings)}
            />
            {isSaved && hasAmalgamAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            2. Estimated total number of teeth with amalgam fillings during your
            life
          </FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter total number"
              value={workingAmalgamTotalTeeth}
              onChange={(e) => {
                if (!isFieldDisabled(amalgamTotalTeeth)) {
                  setWorkingAmalgamTotalTeeth(e.target.value)
                }
              }}
              onBlur={() => setAmalgamTotalTeeth(workingAmalgamTotalTeeth)}
              disabled={isFieldDisabled(amalgamTotalTeeth)}
            />
            {isSaved && hasTotalAmalgamAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            3. Estimated age you had first amalgam placed
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingFirstAmalgamAge}
            onChange={(e) => {
              if (!isFieldDisabled(firstAmalgamAge)) {
                setWorkingFirstAmalgamAge(e.target.value)
              }
            }}
            onBlur={() => setFirstAmalgamAge(workingFirstAmalgamAge)}
            borderColor={amalgamError ? 'red' : undefined}
            borderWidth={amalgamError ? '2px' : undefined}
            disabled={isFieldDisabled(firstAmalgamAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            4. Estimated age you had your last amalgam removed (if you still
            have amalgams leave blank)
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingLastAmalgamAge}
            onChange={(e) => {
              if (!isFieldDisabled(lastAmalgamAge)) {
                setWorkingLastAmalgamAge(e.target.value)
              }
            }}
            onBlur={() => setLastAmalgamAge(workingLastAmalgamAge)}
            borderColor={amalgamError ? 'red' : undefined}
            borderWidth={amalgamError ? '2px' : undefined}
            disabled={isFieldDisabled(lastAmalgamAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Estimated number of years exposed</em>
          </FormLabel>
          <Text
            w="25%"
            fontWeight="bold"
            color={amalgamError ? 'red' : undefined}
            pl={3}
          >
            {computedYearsExposed || '-'}
          </Text>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Estimated exposure number</em>
          </FormLabel>
          <Text w="25%" fontWeight="bold" pl={3}>
            {computedExposureNumber || '-'}
          </Text>
        </Flex>
      </SimpleGrid>
    </>
  )
}

export default AmalgamsSection
